import { modals } from '@mantine/modals';
import CustomVariablesBatchForm from 'app/Private/CustomVariables/CustomVariablesBatchForm/CustomVariablesBatchForm';
import { DisputeReasonForm } from 'app/Private/Reasons/DisputeReasonForm/DisputeReasonForm';
import { PauseReasonForm } from 'app/Private/Reasons/PauseReasonForm/PauseReasonForm';
import { UnrecoverableReasonForm } from 'app/Private/Reasons/UnrecoverableReasonForm/UnrecoverableReasonForm';
import classNames from 'classnames/bind';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, tKeysLoose, useTranslation } from 'locales';
import { isNil, omit, reject } from 'lodash-es';
import { useLoadRecoveryPlans } from 'modules/Settings/Workflows/api';
import { TaskForm, type TaskFormProps } from 'modules/Tasks/TaskForm/TaskForm';
import posthog from 'posthog-js';
import qs from 'query-string';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import MoveRecoveryPlanInvoices from 'shared/action-component/MoveRecoveryPlanInvoices';
import { captureEvent } from 'shared/analytics/capture-event';
import AYSModal from 'shared/components/AYSModal';
import ChooseCollaboratorModal from 'shared/components/ChooseCollaboratorModal';
import ChooseRecoveryPlanModal from 'shared/components/ChooseRecoveryPlanModal';
import ListResModal from 'shared/components/DeleteModal';
import { Icon, IconName } from 'shared/components/Icon';
import { AmlModal } from 'shared/components/InvoiceActionsDropdown/AmlModal';
import { ChooseThirdPartyTypeForm } from 'shared/components/InvoiceActionsDropdown/ChooseThirdPartyTypeForm';
import FormalNoticeForm from 'shared/components/InvoiceActionsDropdown/FormalNotice/FormalNoticeForm';
import { GcollectModal } from 'shared/components/InvoiceActionsDropdown/GcollectModal';
import { LegalCityModal } from 'shared/components/InvoiceActionsDropdown/LegalCityModal/LegalCityModal';
import SelectThirdPartyForm from 'shared/components/InvoiceActionsDropdown/SelectThirdPartyForm';
import MoveRecoveryPlanInvoiceModal from 'shared/components/MoveRecoveryPlanInvoiceModal';
import type { ThirdParties, ThirdPartyType } from 'shared/hooks/use-load-third-parties';
import { ButtonColor } from 'shared/io';
import { error } from 'shared/notifications';
import { treatRecoveryPlan } from 'shared/serializer';
import { useCanUseManualActions } from 'shared/utils/connector';
import { useRefresh } from 'shared/utils/hooks';
import { queryParamParser } from 'shared/utils/view';
import { invoiceActions } from 'store/invoice/invoice.actions';
import { settingsActions } from 'store/settings/settings.actions';
import { DialogShowId, DialogShowSize, dialogHide, showDialog } from 'store/view/view.actions';
import type { Invoice } from 'types/store/invoice-state';
import type { StoreState } from 'types/storeTypes';
import SendingOptionsForm from '../Invoice/SendingOptionsForm';
import styleIdentifiers from './BatchActions.module.scss';
import { buildBatchActionProperties } from './batch-action-analytics';

const styles = classNames.bind(styleIdentifiers);

export enum Categories {
  RECOVERY = 'RECOVERY',
  STATUS = 'STATUS',
  FOLLOW_UP = 'FOLLOW_UP',
  ASSIGNMENT = 'ASSIGNMENT',
  OTHER = 'OTHER',
}

export type BatchActionsProps = {
  selectedInvoices: Invoice[];
  isSelectedAll: boolean;
  reload?: VoidFunction;
};

export default function BatchActions({
  selectedInvoices,
  isSelectedAll: _isSelectedAll,
  reload,
}: BatchActionsProps) {
  const { t } = useTranslation();
  const history = useHistory();

  const company = useCompanyConfiguration();
  const invoicesData = useSelector((state: StoreState) => state.invoice.list);
  const hasManualActionsActive = useCanUseManualActions();

  const [isSelectedAll, setSelectedAll] = useState<boolean>(_isSelectedAll);

  const { doRefresh } = useRefresh();
  const { recoveryPlans = [] } = useLoadRecoveryPlans();

  if (company == null) return null;

  const aysModal = (action_type: any) => (onSubmit: any) => {
    showDialog({
      id: DialogShowId.CONFIRM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.CONFIRMATION),
      children: (
        <AYSModal
          text={t(
            selectedInvoices.length === 1 && !isSelectedAll
              ? tKeysLoose.AYS[`INVOICE_ACTION_${action_type.toUpperCase()}`]
              : tKeysLoose.AYS[`INVOICES_ACTION_${action_type.toUpperCase()}`],
            {
              count: isSelectedAll
                ? invoicesData.metadata.pagination.total_objects
                : selectedInvoices.length,
            },
          )}
          onConfirm={() => onSubmit({})}
        />
      ),
    });
  };

  const batchAction = (action_type: string, action: any) => () => {
    const data = {} as any;

    if (isSelectedAll) {
      data.filter_params = queryParamParser(qs.parse(location.search));
      data.view_id = location.hash.slice(1);
    } else data.invoice_ids = selectedInvoices.map(invoice => invoice.id);

    return action(
      (values: any) =>
        new Promise<void>(resolve => {
          captureEvent(
            posthog,
            'views:batch_action_executed',
            buildBatchActionProperties({
              actionType: action_type,
              isSelectedAll,
              selectedCount: selectedInvoices.length,
              totalObjects: invoicesData.metadata.pagination.total_objects,
              page: location.pathname,
            }),
          );
          invoiceActions.actionsBatchAction({
            data: {
              ...data,
              ...values,
              action_type,
            },
            callback: (res: any) => {
              dialogHide(DialogShowId.CONFIRM);
              showDialog({
                id: DialogShowId.CUSTOM,
                size: DialogShowSize.MEDIUM,
                title: t(i18nKeys.RESULT),
                children: <ListResModal data={res} />,
              });
              setSelectedAll(false);
              doRefresh();
              reload?.();
              resolve();
            },
            actionFailure: () => resolve(),
          });
        }),
    );
  };

  const paymentPlan = () => {
    const data = {} as any;
    if (isSelectedAll) {
      data.filter_params = queryParamParser(qs.parse(location.search));
      data.view_id = location.hash.slice(1);
    } else {
      data.invoice_ids = selectedInvoices.map(invoice => invoice.id);
    }

    invoiceActions.actionsBatchAction({
      data: {
        ...data,
        action_type: 'payment_plan',
      },
      callback: (response: any) => {
        const { debtor_id, invoice_ids } = response[0];
        history.push(`/payment-plans/new?debtor_id=${debtor_id}&invoice_ids=${invoice_ids}`);
        dialogHide(DialogShowId.CUSTOM);
      },
    });
  };

  const pause = () => {
    modals.open({
      size: 'sm',
      title: t(i18nKeys.CLIENT.PAUSE),
      children: (
        <PauseReasonForm
          onSubmit={values => batchAction('pause', (submit: any) => submit(values))()}
        />
      ),
    });
  };

  const dispute = () => {
    modals.open({
      size: 'sm',
      title: t('INVOICE.DISPUTED'),
      children: (
        <DisputeReasonForm
          onSubmit={values => batchAction('dispute', (submit: any) => submit(values))()}
        />
      ),
    });
  };

  const markAsUnrecoverable = () => {
    modals.open({
      size: 'sm',
      title: t('INVOICE.UNRECOVERABLE'),
      children: (
        <UnrecoverableReasonForm
          onSubmit={values =>
            batchAction('mark_as_unrecoverable', (submit: any) => submit(values))()
          }
        />
      ),
    });
  };

  const selectStepPlan = (
    newPlan: any,
    plans: any,
    counter: any,
    maxItems: any,
    callback: any,
    step_switchs: any[] = [],
  ) => {
    MoveRecoveryPlanInvoices({
      newPlan,
      oldPlan: plans[counter - 1],
      title: t(i18nKeys.PLAN.MOVE_INVOICES),
      onSubmit: (steps_switch: any) => {
        step_switchs.push({
          to: newPlan.id,
          from: plans[counter - 1].id,
          steps_switch,
        });
        if (counter === maxItems) {
          callback(step_switchs);
        } else {
          selectStepPlan(newPlan, plans, counter + 1, maxItems, callback, step_switchs);
        }
      },
    });
  };

  const movePlan = async () => {
    let scope = {
      scope: 'invoice',
    } as any;
    if (isSelectedAll) {
      scope = { scope: 'invoice', ...qs.parse(location.search) };
      scope.view_id = location.hash.slice(1);
    } else {
      scope.invoice_ids = selectedInvoices.map(invoice => invoice.id);
    }

    const planId: string | null = await new Promise(resolve => {
      showDialog({
        id: DialogShowId.CUSTOM,
        size: DialogShowSize.MEDIUM,
        title: t(i18nKeys.PLAN.WHERE_MOVE_INVOICES),
        children: (
          <ChooseRecoveryPlanModal
            recoverPlans={recoveryPlans}
            onClick={selectedPlan => resolve(selectedPlan ?? null)}
          />
        ),
      });
    });

    const response: any = await new Promise(resolve => {
      settingsActions.getRecoveryPlan({
        id: Number(planId),
        callback: resolve,
      });
    });

    const { newPlan, out_of_plan, ...res } = await new Promise<any>(resolve => {
      settingsActions.getPlansInvoices({
        data: scope,
        callback: (props: any) =>
          resolve({
            ...props,
            newPlan: treatRecoveryPlan(response.data),
          }),
      });
    });

    const plans = res.data.map((plan: any) => treatRecoveryPlan(plan));

    if (plans.length === 0 && !out_of_plan) {
      error({
        text: i18nKeys.ERROR.NO_INVOICE_IN_PLAN,
      });
    } else {
      const stepIndex = await new Promise<number | null>(resolve => {
        if (out_of_plan) {
          showDialog({
            id: DialogShowId.CUSTOM,
            size: DialogShowSize.LARGE,
            title: t(i18nKeys.PLAN.MOVE_INVOICES),
            children: <MoveRecoveryPlanInvoiceModal onSubmit={resolve} plan={newPlan} />,
          });
        } else {
          resolve(null);
        }
      });

      const plansSwitch = await new Promise<Array<any>>(resolve => {
        if (plans.length > 0) {
          selectStepPlan(newPlan, plans, 1, plans.length, resolve);
        } else {
          resolve([]);
        }
      });

      if (stepIndex !== null) {
        plansSwitch.push({
          to: planId,
          from: null,
          steps_switch: [{ from: null, to: newPlan.recovery_steps[stepIndex!].id }],
        });
      }

      settingsActions.switchPlansInvoices({
        id: Number(planId),
        data: {
          plans_switch: plansSwitch,
          ...scope,
        },
        callback: ({ data: _data }: any) => {
          showDialog({
            id: DialogShowId.CUSTOM,
            size: DialogShowSize.MEDIUM,
            title: t(i18nKeys.RESULT),
            children: <ListResModal data={_data} />,
          });
        },
      });
    }
  };

  const assignToAccManager = (onSubmit: any) => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.CHOOSE_ACCOUNT_MANAGER),
      children: <ChooseCollaboratorModal onSubmit={onSubmit} />,
    });
  };

  const taskDetails = () => {
    dialogHide(DialogShowId.CUSTOM);

    const data: TaskFormProps['batchData'] = {
      filter_params: isSelectedAll ? queryParamParser(qs.parse(location.search)) : undefined,
      view_id: isSelectedAll ? location.hash.slice(1) : undefined,
      debtor_ids: undefined,
      invoice_ids: isSelectedAll ? undefined : selectedInvoices?.map(({ id }) => String(id)),
    };

    modals.open({
      title: t(i18nKeys.FORM.TASK.NEW),
      size: 'xl',
      children: <TaskForm batchData={data} />,
    });
  };

  const customVariablesDetails = (onSubmit: any) => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.SETTINGS.CUSTOM_VARIABLES.LONG_TITLE),
      children: <CustomVariablesBatchForm onSubmit={onSubmit} resourceType="invoice" />,
    });
  };

  const { default_templates } = company;

  const additionalReminderDetails = (onSubmit: any) => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.INVOICE.SENDING_OPTIONS),
      children: (
        <SendingOptionsForm
          customText={t(i18nKeys.INVOICE.SENDING_OPTIONS_BATCH_SUMMARY)}
          onSubmit={onSubmit}
          setTemplate={() => {}}
          initialValues={{
            template_id: default_templates.additional_reminder.id,
            send_email: false,
            send_post: false,
          }}
        />
      ),
    });
  };

  const formalNoticeDetails = (onSubmit: any) => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.SMALL,
      title: t(i18nKeys.INVOICE.SENDING_OPTIONS),
      children: (
        <FormalNoticeForm
          batch
          customText={t(i18nKeys.INVOICE.SENDING_OPTIONS_BATCH_SUMMARY)}
          onSubmit={onSubmit}
          onChangeTemplate={() => {}}
          initialValues={{
            template_id: default_templates.formal_notice.id,
            send_email: true,
          }}
        />
      ),
    });
  };

  const openGcollectFlow = (data: { third_party_company_id: number }) => {
    dialogHide(DialogShowId.CUSTOM);
    modals.open({
      title: t(i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.GCOLLECT.CONFIRM_RATES),
      centered: true,
      size: 'xl',
      children: (
        <GcollectModal
          gcollectId={data.third_party_company_id}
          invoiceIds={selectedInvoices.map(({ id }) => id)}
        />
      ),
    });
  };

  const openAMLModal = (
    data: { third_party_company_id: number },
    onSubmit: (data: any) => void,
  ) => {
    dialogHide(DialogShowId.CUSTOM);
    modals.open({
      title: t(i18nKeys.AML_MODAL.TITLE),
      centered: true,
      size: '70%',
      children: (
        <AmlModal
          invoiceIds={selectedInvoices.map(({ id }) => id)}
          thirdPartyCompanyId={data.third_party_company_id}
          additionalInvoiceIds={[]}
          onSubmit={onSubmit}
        />
      ),
    });
  };

  const openLegalCityModal = (
    data: { third_party_company_id: number },
    _onSubmit: (data: any) => void,
  ) => {
    dialogHide(DialogShowId.CUSTOM);
    modals.open({
      title: 'LegalCity',
      centered: true,
      children: (
        <LegalCityModal
          legalcityId={String(data.third_party_company_id)}
          invoiceIds={selectedInvoices.map(({ id }) => id)}
        />
      ),
    });
  };

  const openThirdParty = (
    thirdParties: ThirdParties,
    selectedThirdPartyType: ThirdPartyType,
    onSubmitFunction: Function,
  ) => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.MEDIUM,
      title: t(i18nKeys.FORM.CHOOSE_THIRD_PARTY_TYPE.TITLE),
      children: (
        <SelectThirdPartyForm
          onSubmit={onSubmitFunction}
          onSubmitGcollect={openGcollectFlow}
          onSubmitLegalCity={openLegalCityModal}
          onSubmitAML={openAMLModal}
          thirdParties={thirdParties}
          selectedThirdPartyType={selectedThirdPartyType}
        />
      ),
    });
  };

  const thirdPartyDetails = (onSubmit: any) => {
    showDialog({
      id: DialogShowId.CUSTOM,
      size: DialogShowSize.LARGE,
      title: t(i18nKeys.FORM.CHOOSE_THIRD_PARTY_TYPE.TITLE),
      children: (
        <ChooseThirdPartyTypeForm
          invoices={selectedInvoices.map(invoice => ({
            ...omit(invoice, 'attributes'),
            // @ts-expect-error incorrectly typed
            ...invoice.attributes,
          }))}
          onSubmit={(selectedThirdPartyType, thirdParties) =>
            openThirdParty(thirdParties, selectedThirdPartyType, onSubmit)
          }
        />
      ),
    });
  };

  const allActions = [
    {
      name: i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.SEND_REMINDER,
      function: batchAction('additional_reminder', additionalReminderDetails),
      icon: IconName.SEND,
      category: Categories.RECOVERY,
    },
    {
      name: i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.SEND_FORMAL_NOTICE,
      function: batchAction('formal_notice', formalNoticeDetails),
      icon: IconName.FORMAL_NOTICE,
      category: Categories.RECOVERY,
    },
    {
      name: i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.SEND_TO_THIRD_PARTY,
      function: batchAction('third_party', thirdPartyDetails),
      icon: IconName.LAW,
      category: Categories.RECOVERY,
    },
    {
      name: i18nKeys.PAYMENT_PLAN.TITLE.NEW,
      function: paymentPlan,
      icon: IconName.PAYMENT_BOOK,
      category: Categories.RECOVERY,
    },
    hasManualActionsActive
      ? {
          name: i18nKeys.MARK_AS_PAID,
          function: batchAction('mark_as_paid', aysModal('mark_as_paid')),
          icon: IconName.CHECK,
          category: Categories.STATUS,
        }
      : null,
    {
      name: i18nKeys.MARK_AS_DISPUTED,
      function: dispute,
      icon: IconName.ALERT_CIRCLE,
      category: Categories.STATUS,
    },
    {
      name: i18nKeys.UNDISPUTED,
      function: batchAction('undispute', aysModal('undispute')),
      icon: IconName.BOX_RIBBON,
      category: Categories.STATUS,
    },
    {
      name: i18nKeys.MARK_AS_UNRECOVERABLE,
      function: markAsUnrecoverable,
      icon: IconName.QUESTION,
      category: Categories.STATUS,
    },
    {
      name: i18nKeys.INVOICE.MOVE_PLAN,
      function: movePlan,
      icon: IconName.REPLACE,
      category: Categories.ASSIGNMENT,
    },
    {
      name: i18nKeys.ASSIGN_ACCOUNT_MANAGER_SHORT,
      function: batchAction('assign', assignToAccManager),
      icon: IconName.USER,
      category: Categories.ASSIGNMENT,
    },
    {
      name: i18nKeys.INVOICE.PAUSE_THE_FOLLOW_UP,
      function: pause,
      icon: IconName.PAUSE,
      category: Categories.FOLLOW_UP,
    },
    {
      name: i18nKeys.INVOICE.RESUME_FOLLUW_UP,
      function: batchAction('unpause', aysModal('unpause')),
      icon: IconName.PLAY,
      category: Categories.FOLLOW_UP,
    },
    {
      name: i18nKeys.FOLLOW_UP.INVOICE_TO_PROCESS.TAKE_OUT_OF_PROCESS,
      function: batchAction('to_be_processed', aysModal('to_be_processed')),
      icon: IconName.ROBOT,
      category: Categories.FOLLOW_UP,
    },
    {
      name: i18nKeys.FORM.TASK.NEW,
      function: taskDetails,
      icon: IconName.COMMENT,
      category: Categories.ASSIGNMENT,
      disabled: !company.package.can_use_tasks,
    },
    {
      name: i18nKeys.BATCH.ACTIONS.CUSTOM_VARIABLES,
      function: batchAction('custom_variables', customVariablesDetails),
      icon: IconName.PENCIL,
      category: Categories.OTHER,
      disabled: !company.package.can_use_custom_variables,
    },
    {
      name: i18nKeys.IGNORE_LATE_FEES,
      function: batchAction('ignore_late_fees', aysModal('ignore_late_fees')),
      icon: IconName.SIMPLE_REMOVE,
      category: Categories.OTHER,
    },
    hasManualActionsActive
      ? {
          name: i18nKeys.DELETE,
          function: batchAction('delete', aysModal('delete')),
          icon: IconName.TRASH_SIMPLE,
          category: Categories.OTHER,
          color: ButtonColor.RED,
        }
      : null,
  ];

  const actions = reject(allActions, isNil) as Array<NonNullable<(typeof allActions)[number]>>;

  return (
    <div className={styles('batch-actions-wrapper')}>
      {Object.values(Categories).map(category => (
        <div key={category}>
          <h5>{t(i18nKeys.BATCH.CATEGORIES[category])}</h5>
          <hr />
          <div className={styles('batch-actions')}>
            {actions
              .filter(action => action.category === category)
              .map(action => (
                <div
                  className={styles('batch-action', action.color, action.disabled && 'disabled')}
                  onClick={action.function}
                  key={action.name}
                >
                  <Icon name={action.icon} />
                  <p>{t(action.name)}</p>
                </div>
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}
